import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ArrowRight, Shield, Users, Award, UserCog, DollarSign, User as UserIcon, GraduationCap, Building2, Briefcase, Handshake } from 'lucide-react';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/Card';
import { cn } from '../lib/utils';
const ROLE_CATEGORIES = [{
  category: 'Admin & Pengelola',
  roles: [{
    id: 'super_admin' as UserRole,
    name: 'Super Admin',
    description: 'Akses penuh sistem',
    icon: Shield,
    email: 'admin@atios.or.id',
    color: 'text-error',
    bgColor: 'bg-error-light'
  }, {
    id: 'komite_pusat' as UserRole,
    name: 'Komite Pusat',
    description: 'Pengawasan organisasi',
    icon: Award,
    email: 'siti@atios.or.id',
    color: 'text-warning',
    bgColor: 'bg-warning-light'
  }, {
    id: 'koordinator_acara' as UserRole,
    name: 'Koordinator Acara',
    description: 'Kelola acara',
    icon: UserCog,
    email: 'ahmad@atios.or.id',
    color: 'text-success',
    bgColor: 'bg-success-light'
  }, {
    id: 'bendahara' as UserRole,
    name: 'Bendahara',
    description: 'Kelola keuangan',
    icon: DollarSign,
    email: 'dewi@atios.or.id',
    color: 'text-warning',
    bgColor: 'bg-warning-light'
  }]
}, {
  category: 'Ketua Chapter',
  roles: [{
    id: 'ketua_chapter' as UserRole,
    name: 'Ketua Chapter',
    description: 'Chapter regional',
    icon: Users,
    email: 'budi@atios.or.id',
    color: 'text-atios-primary',
    bgColor: 'bg-atios-light'
  }, {
    id: 'ketua_student_chapter' as UserRole,
    name: 'Ketua Student Chapter',
    description: 'Chapter mahasiswa',
    icon: GraduationCap,
    email: 'andi@atios.or.id',
    color: 'text-atios-primary',
    bgColor: 'bg-atios-light'
  }]
}, {
  category: 'Anggota',
  roles: [{
    id: 'anggota_individu' as UserRole,
    name: 'Anggota Individu',
    description: 'Keanggotaan pribadi',
    icon: UserIcon,
    email: 'john@atios.or.id',
    color: 'text-neutral-700',
    bgColor: 'bg-neutral-100'
  }, {
    id: 'anggota_organisasi' as UserRole,
    name: 'Anggota Organisasi',
    description: 'Keanggotaan organisasi',
    icon: Building2,
    email: 'contact@techindonesia.com',
    color: 'text-neutral-700',
    bgColor: 'bg-neutral-100'
  }, {
    id: 'anggota_badan_usaha' as UserRole,
    name: 'Anggota Badan Usaha',
    description: 'Keanggotaan perusahaan',
    icon: Briefcase,
    email: 'info@digitalsolutions.id',
    color: 'text-neutral-700',
    bgColor: 'bg-neutral-100'
  }, {
    id: 'mitra_asosiasi' as UserRole,
    name: 'Mitra Asosiasi',
    description: 'Kemitraan strategis',
    icon: Handshake,
    email: 'partner@techcorp.com',
    color: 'text-neutral-700',
    bgColor: 'bg-neutral-100'
  }]
}];
export function LoginPage() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleRoleSelect = (role: UserRole, roleEmail: string) => {
    setSelectedRole(role);
    setEmail(roleEmail);
    setError('');
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) {
      setError('Silakan pilih peran');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await login(email, password, selectedRole);
      switch (selectedRole) {
        case 'super_admin':
          navigate('/admin/dashboard');
          break;
        case 'ketua_chapter':
        case 'ketua_student_chapter':
          navigate('/chapter/dashboard');
          break;
        case 'komite_pusat':
          navigate('/committee/dashboard');
          break;
        case 'koordinator_acara':
          navigate('/events/manage');
          break;
        case 'bendahara':
          navigate('/finance/dashboard');
          break;
        default:
          navigate('/dashboard');
      }
    } catch (err) {
      setError('Kredensial tidak valid. Gunakan password: "demo123"');
    } finally {
      setIsLoading(false);
    }
  };
  if (!selectedRole) {
    return <div className="min-h-screen w-full flex bg-white" data-id="element-3679">
        <div className="w-full flex items-center justify-center p-8" data-id="element-3680">
          <div className="w-full max-w-6xl" data-id="element-3681">
            <div className="text-center mb-12" data-id="element-3682">
              <img src="https://cdn.magicpatterns.com/uploads/fWeCgNWwEpM2aXTFp4cDwR/ATIOS-logo-forum.png" alt="ATIOS Logo" className="h-10 mx-auto mb-8" data-id="element-3683" />
              <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-3684">
                Selamat Datang di ATIOS
              </h1>
              <p className="text-neutral-600" data-id="element-3685">
                Pilih peran Anda untuk melanjutkan
              </p>
            </div>

            {ROLE_CATEGORIES.map(category => <div key={category.category} className="mb-8" data-id="element-3686">
                <h2 className="text-lg font-semibold text-neutral-900 mb-4 px-2" data-id="element-3687">
                  {category.category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3688">
                  {category.roles.map(role => <Card key={role.id} className="cursor-pointer hover:shadow-md hover:border-atios-primary transition-all" onClick={() => handleRoleSelect(role.id, role.email)} data-id="element-3689">
                      <CardContent className="p-6" data-id="element-3690">
                        <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center mb-4', role.bgColor)} data-id="element-3691">
                          <role.icon className={cn('w-6 h-6', role.color)} data-id="element-3692" />
                        </div>
                        <h3 className="font-semibold text-neutral-900 mb-1" data-id="element-3693">
                          {role.name}
                        </h3>
                        <p className="text-sm text-neutral-600 mb-3" data-id="element-3694">
                          {role.description}
                        </p>
                        <p className="text-xs text-neutral-500" data-id="element-3695">{role.email}</p>
                      </CardContent>
                    </Card>)}
                </div>
              </div>)}

            <div className="text-center mt-8" data-id="element-3696">
              <p className="text-sm text-neutral-500" data-id="element-3697">
                Kredensial demo: Semua password berfungsi (coba "demo123")
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  const selectedRoleData = ROLE_CATEGORIES.flatMap(cat => cat.roles).find(r => r.id === selectedRole)!;
  return <div className="min-h-screen w-full flex bg-white" data-id="element-3698">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-neutral-50 items-center justify-center p-16" data-id="element-3699">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(48,165,219,0.08),transparent_50%)]" data-id="element-3700" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(48,165,219,0.05),transparent_50%)]" data-id="element-3701" />

        <div className="relative z-10 max-w-lg" data-id="element-3702">
          <div className="mb-8" data-id="element-3703">
            <img src="https://cdn.magicpatterns.com/uploads/fWeCgNWwEpM2aXTFp4cDwR/ATIOS-logo-forum.png" alt="ATIOS Logo" className="h-12 mb-8" data-id="element-3704" />
          </div>

          <h1 className="text-4xl font-semibold tracking-tight mb-6 text-neutral-900 leading-tight" data-id="element-3705">
            Selamat Datang di
            <span className="block text-atios-primary mt-2" data-id="element-3706">ATIOS</span>
          </h1>

          <p className="text-base text-neutral-600 leading-relaxed mb-12" data-id="element-3707">
            Platform keanggotaan profesional untuk Asosiasi Teknologi Informasi
            dan Open Source Indonesia
          </p>

          <div className="space-y-6" data-id="element-3708">
            <div className="flex items-start gap-4" data-id="element-3709">
              <div className="h-10 w-10 rounded-lg bg-atios-light flex items-center justify-center flex-shrink-0" data-id="element-3710">
                <Users className="w-5 h-5 text-atios-primary" data-id="element-3711" />
              </div>
              <div data-id="element-3712">
                <h3 className="font-medium text-neutral-900 mb-1" data-id="element-3713">
                  Jaringan Profesional
                </h3>
                <p className="text-sm text-neutral-600" data-id="element-3714">
                  Terhubung dengan 10.000+ profesional TI di seluruh Indonesia
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-3715">
              <div className="h-10 w-10 rounded-lg bg-atios-light flex items-center justify-center flex-shrink-0" data-id="element-3716">
                <Award className="w-5 h-5 text-atios-primary" data-id="element-3717" />
              </div>
              <div data-id="element-3718">
                <h3 className="font-medium text-neutral-900 mb-1" data-id="element-3719">
                  Sertifikasi & Pelatihan
                </h3>
                <p className="text-sm text-neutral-600" data-id="element-3720">
                  Akses program pelatihan dan sertifikasi profesional
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-3721">
              <div className="h-10 w-10 rounded-lg bg-atios-light flex items-center justify-center flex-shrink-0" data-id="element-3722">
                <Shield className="w-5 h-5 text-atios-primary" data-id="element-3723" />
              </div>
              <div data-id="element-3724">
                <h3 className="font-medium text-neutral-900 mb-1" data-id="element-3725">
                  Bursa Lowongan Kerja
                </h3>
                <p className="text-sm text-neutral-600" data-id="element-3726">
                  Akses eksklusif ke peluang dari perusahaan terkemuka
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white" data-id="element-3727">
        <div className="w-full max-w-[400px]" data-id="element-3728">
          <div className="lg:hidden mb-8 text-center" data-id="element-3729">
            <img src="https://cdn.magicpatterns.com/uploads/fWeCgNWwEpM2aXTFp4cDwR/ATIOS-logo-forum.png" alt="ATIOS Logo" className="h-10 mx-auto" data-id="element-3730" />
          </div>

          <div className="mb-6" data-id="element-3731">
            <button onClick={() => setSelectedRole(null)} className="flex items-center gap-3 p-4 rounded-lg border border-neutral-200 hover:border-atios-primary transition-all w-full" data-id="element-3732">
              <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', selectedRoleData.bgColor)} data-id="element-3733">
                <selectedRoleData.icon className={cn('w-5 h-5', selectedRoleData.color)} data-id="element-3734" />
              </div>
              <div className="flex-1 text-left" data-id="element-3735">
                <p className="text-sm font-medium text-neutral-900" data-id="element-3736">
                  {selectedRoleData.name}
                </p>
                <p className="text-xs text-neutral-500" data-id="element-3737">
                  Klik untuk ganti peran
                </p>
              </div>
            </button>
          </div>

          <div className="mb-8" data-id="element-3738">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-2" data-id="element-3739">
              Masuk sebagai {selectedRoleData.name}
            </h2>
            <p className="text-neutral-600 text-sm" data-id="element-3740">Akses portal ATIOS Anda</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-id="element-3741">
            <Input label="Alamat Email" type="email" placeholder="nama@contoh.com" value={email} onChange={e => setEmail(e.target.value)} required data-id="element-3742" />

            <div className="space-y-3" data-id="element-3743">
              <Input label="Kata Sandi" type="password" placeholder="Masukkan kata sandi Anda" value={password} onChange={e => setPassword(e.target.value)} required data-id="element-3744" />
              <div className="flex items-center justify-between text-sm" data-id="element-3745">
                <label className="flex items-center gap-2 cursor-pointer group" data-id="element-3746">
                  <input type="checkbox" className="rounded border-neutral-300 text-atios-primary focus:ring-atios-primary" data-id="element-3747" />
                  <span className="text-neutral-600 group-hover:text-neutral-900 transition-colors" data-id="element-3748">
                    Ingat saya
                  </span>
                </label>
                <Link to="/lupa-password" className="text-atios-primary hover:text-atios-secondary transition-colors font-medium" data-id="element-3749">
                  Lupa kata sandi?
                </Link>
              </div>
            </div>

            {error && <div className="p-3 rounded-lg bg-error-light border border-error text-error text-sm" data-id="element-3750">
                {error}
              </div>}

            <div className="space-y-4 pt-2" data-id="element-3751">
              <Button type="submit" fullWidth size="lg" disabled={isLoading} data-id="element-3752">
                {isLoading ? <span className="flex items-center gap-2" data-id="element-3753">
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3754" />
                    Sedang masuk...
                  </span> : <span className="flex items-center gap-2" data-id="element-3755">
                    Masuk
                    <ArrowRight className="w-4 h-4" data-id="element-3756" />
                  </span>}
              </Button>

              <div className="relative" data-id="element-3757">
                <div className="absolute inset-0 flex items-center" data-id="element-3758">
                  <span className="w-full border-t border-neutral-200" data-id="element-3759" />
                </div>
                <div className="relative flex justify-center text-xs uppercase" data-id="element-3760">
                  <span className="bg-white px-3 text-neutral-500 font-medium" data-id="element-3761">
                    Atau
                  </span>
                </div>
              </div>

              <Link to="/register/step1" className="block" data-id="element-3762">
                <Button variant="secondary" fullWidth type="button" data-id="element-3763">
                  Belum punya akun? Daftar
                </Button>
              </Link>
            </div>
          </form>

          <p className="text-center text-xs text-neutral-500 mt-8" data-id="element-3764">
            Dengan masuk, Anda menyetujui{' '}
            <Link to="/syarat-ketentuan" className="text-atios-primary hover:underline" data-id="element-3765">
              Syarat & Ketentuan
            </Link>{' '}
            dan{' '}
            <Link to="/kebijakan-privasi" className="text-atios-primary hover:underline" data-id="element-3766">
              Kebijakan Privasi
            </Link>
          </p>
        </div>
      </div>
    </div>;
}